import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * MerchantSearchSelect — debounced combobox for selecting or creating a merchant.
 *
 * - Searches merchants as the user types (300 ms debounce)
 * - Shows name, type badge, and country flag in the dropdown
 * - "Add new merchant: {query}" option at the bottom creates inline
 * - Compatible with React Hook Form via <Controller>
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Plus, X, Loader2, Store } from 'lucide-react';
import { merchantsApi } from '../../lib/api';
import { cn } from '../../lib/utils';
// ── Merchant type labels ───────────────────────────────────────────────────────
const TYPE_LABELS = {
    wine_merchant: 'Merchant',
    auction_house: 'Auction',
    producer: 'Producer',
    private: 'Private',
    other: 'Other',
};
const TYPE_COLOURS = {
    wine_merchant: 'bg-blue-100 text-blue-700',
    auction_house: 'bg-amber-100 text-amber-700',
    producer: 'bg-green-100 text-green-700',
    private: 'bg-purple-100 text-purple-700',
    other: 'bg-gray-100 text-gray-600',
};
// ── Country flag helper ────────────────────────────────────────────────────────
function countryFlag(code) {
    if (!code || code.length !== 2)
        return '';
    const offset = 127397;
    return String.fromCodePoint(...[...code.toUpperCase()].map((c) => c.charCodeAt(0) + offset));
}
// ── Inline create form ─────────────────────────────────────────────────────────
const MERCHANT_TYPES = [
    { value: 'wine_merchant', label: 'Wine Merchant' },
    { value: 'auction_house', label: 'Auction House' },
    { value: 'producer', label: 'Producer' },
    { value: 'private', label: 'Private' },
    { value: 'other', label: 'Other' },
];
function InlineCreateForm({ initialName, onCreated, onCancel }) {
    const [name, setName] = useState(initialName);
    const [type, setType] = useState('wine_merchant');
    const [website, setWebsite] = useState('');
    const [country, setCountry] = useState('GB');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    async function handleSave() {
        if (!name.trim())
            return;
        setSaving(true);
        setErr(null);
        try {
            const m = await merchantsApi.create({
                name: name.trim(),
                merchantType: type,
                website: website.trim() || undefined,
                countryCode: country.trim().toUpperCase() || undefined,
            });
            onCreated(m);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'Failed to create merchant');
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsxs("div", { className: "p-3 border-t border-gray-200 bg-gray-50 space-y-3", children: [_jsx("p", { className: "text-xs font-semibold text-gray-500 uppercase tracking-wide", children: "New merchant" }), _jsxs("div", { className: "space-y-2", children: [_jsx("input", { type: "text", value: name, onChange: (e) => setName(e.target.value), placeholder: "Merchant name", className: "input-field w-full text-sm", autoFocus: true }), _jsxs("div", { className: "grid grid-cols-2 gap-2", children: [_jsx("select", { value: type, onChange: (e) => setType(e.target.value), className: "input-field text-sm", children: MERCHANT_TYPES.map((t) => (_jsx("option", { value: t.value, children: t.label }, t.value))) }), _jsx("input", { type: "text", value: country, onChange: (e) => setCountry(e.target.value.toUpperCase()), maxLength: 2, placeholder: "Country (GB)", className: "input-field text-sm" })] }), _jsx("input", { type: "text", value: website, onChange: (e) => setWebsite(e.target.value), placeholder: "Website (optional)", className: "input-field w-full text-sm" })] }), err && _jsx("p", { className: "text-xs text-red-600", children: err }), _jsxs("div", { className: "flex gap-2", children: [_jsx("button", { type: "button", onClick: handleSave, disabled: saving || !name.trim(), className: "btn-primary btn-sm", children: saving ? _jsx(Loader2, { className: "h-3 w-3 animate-spin" }) : 'Add merchant' }), _jsx("button", { type: "button", onClick: onCancel, className: "btn-secondary btn-sm", children: "Cancel" })] })] }));
}
export function MerchantSearchSelect({ value, onChange, error, placeholder = 'Search merchants…', disabled = false, }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const [showCreate, setShowCreate] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    // Load the selected merchant name on mount / value change
    useEffect(() => {
        if (!value) {
            setSelected(null);
            return;
        }
        if (selected?.id === value)
            return;
        merchantsApi.get(value).then(setSelected).catch(() => setSelected(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    // Debounced search
    useEffect(() => {
        if (!open)
            return;
        const id = setTimeout(async () => {
            setLoading(true);
            try {
                const data = await merchantsApi.list({ q: query, includeSystem: true });
                setResults(data.merchants ?? []);
            }
            catch {
                setResults([]);
            }
            finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(id);
    }, [query, open]);
    // Close on outside click
    useEffect(() => {
        function handler(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
                setShowCreate(false);
            }
        }
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    const handleOpen = useCallback(() => {
        if (disabled)
            return;
        setQuery('');
        setOpen(true);
        setShowCreate(false);
        setTimeout(() => inputRef.current?.focus(), 0);
    }, [disabled]);
    const handleSelect = useCallback((merchant) => {
        setSelected(merchant);
        onChange(merchant.id, merchant.name);
        setOpen(false);
        setShowCreate(false);
    }, [onChange]);
    const handleClear = useCallback((e) => {
        e.stopPropagation();
        setSelected(null);
        onChange(null, null);
    }, [onChange]);
    const handleCreated = useCallback((merchant) => {
        handleSelect(merchant);
    }, [handleSelect]);
    return (_jsxs("div", { ref: containerRef, className: "relative", children: [selected ? (_jsxs("div", { className: cn('input-field flex items-center gap-2 cursor-pointer', error && 'border-red-500', disabled && 'opacity-50 cursor-not-allowed'), onClick: disabled ? undefined : handleOpen, children: [_jsx(Store, { className: "h-4 w-4 text-gray-400 flex-shrink-0" }), _jsx("span", { className: "flex-1 truncate text-sm", children: selected.name }), _jsx("span", { className: cn('rounded px-1.5 py-0.5 text-xs font-medium', TYPE_COLOURS[selected.merchantType] ?? TYPE_COLOURS.other), children: TYPE_LABELS[selected.merchantType] ?? 'Other' }), selected.countryCode && (_jsx("span", { className: "text-base leading-none", children: countryFlag(selected.countryCode) })), !disabled && (_jsx("button", { type: "button", onClick: handleClear, className: "ml-auto text-gray-400 hover:text-gray-600", children: _jsx(X, { className: "h-3.5 w-3.5" }) }))] })) : (_jsxs("div", { className: cn('input-field flex items-center gap-2 cursor-pointer text-gray-400', error && 'border-red-500', disabled && 'opacity-50 cursor-not-allowed'), onClick: handleOpen, children: [_jsx(Search, { className: "h-4 w-4 flex-shrink-0" }), _jsx("span", { className: "text-sm", children: placeholder })] })), open && (_jsxs("div", { className: "absolute z-50 mt-1 w-full min-w-72 rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden", children: [_jsx("div", { className: "p-2 border-b border-gray-100", children: _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Search, { className: "h-4 w-4 text-gray-400 flex-shrink-0" }), _jsx("input", { ref: inputRef, type: "text", value: query, onChange: (e) => setQuery(e.target.value), placeholder: "Type to search\u2026", className: "flex-1 text-sm outline-none" }), loading && _jsx(Loader2, { className: "h-3.5 w-3.5 animate-spin text-gray-400" })] }) }), _jsxs("ul", { className: "max-h-52 overflow-y-auto", children: [results.length === 0 && !loading && (_jsx("li", { className: "px-3 py-2 text-sm text-gray-400", children: "No merchants found" })), results.map((m) => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => handleSelect(m), className: "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left", children: [_jsx(Store, { className: "h-4 w-4 text-gray-300 flex-shrink-0" }), _jsx("span", { className: "flex-1 truncate font-medium text-gray-900", children: m.name }), _jsx("span", { className: cn('rounded px-1.5 py-0.5 text-xs font-medium', TYPE_COLOURS[m.merchantType] ?? TYPE_COLOURS.other), children: TYPE_LABELS[m.merchantType] ?? 'Other' }), m.countryCode && (_jsx("span", { className: "text-base leading-none", children: countryFlag(m.countryCode) })), m.isSystemRecord && (_jsx("span", { className: "text-xs text-gray-400", children: "global" }))] }) }, m.id)))] }), !showCreate && (_jsx("div", { className: "border-t border-gray-100", children: _jsxs("button", { type: "button", onClick: () => setShowCreate(true), className: "w-full flex items-center gap-2 px-3 py-2 text-sm text-wine-700 hover:bg-wine-50", children: [_jsx(Plus, { className: "h-4 w-4" }), query ? `Add "${query}" as new merchant` : 'Add new merchant'] }) })), showCreate && (_jsx(InlineCreateForm, { initialName: query, onCreated: handleCreated, onCancel: () => setShowCreate(false) }))] })), error && _jsx("p", { className: "mt-1 text-xs text-red-600", children: error })] }));
}
